/*
 * Product Quick View
 *   Modular class to handle product quick view functionality
 *   Manages opening/closing the drawer and loading product content
 *
 * Version:
 *   1.0.0 - 2025/10/07
 */

import { CLASS_ACTIVE, CLASS_LOADED, CLASS_LOADING, CLASS_SCROLL_DISABLED, CLASS_DISABLED, CLASS_DRAWER_OPEN } from '../../tools/constants/classes';
import { ProductForm, SEL_FORM_CONTAINER } from "../../objects/product/product-form";
import { EventVariantChanged, EVT_VARIANT_CHANGED } from '../../objects/product/variant-selector';
import { EVT_SOLD_OUT_STATUS_UPDATED } from '../../objects/product/swatches';
import { rebindContentDrawerEvents } from '../../objects/content-drawer';
import { rebindWishlist } from '../../objects/swym-wishlist';
import { t } from '@process-creative/slate-theme-tools';

// Selectors
const SELECTOR_CONTAINER = '[data-product-quick-view-container]';
const SELECTOR_QUICK_VIEW_PLACEHOLDER = '[data-product-quick-view]';
const SELECTOR_BACKGROUND = '[data-product-quick-view-background]';
const SELECTOR_CLOSE = '[data-product-quick-view-close]';
const SELECTOR_QUICK_VIEW_BTN = '[data-product-quick-view-btn]';
const SELECTOR_READ_MORE_BUTTON = '[data-read-more-button]';
const SELECTOR_WISHLIST_BUTTON = '[data-wishlist-button]';
const SELECTOR_PRODUCT_DETAILS = '[data-product-details]';
const SELECTOR_PRODUCT_DETAIL_LOADING = '[data-product-loading]';
const SELECTOR_PRODUCT_MAIN = '[data-product-main]';
const SELECTOR_CART_CONTAINER = '[data-cart-container]';
const SELECTOR_PRODUCT_THUMBNAIL = '[data-product-thumbnail]';

const PREFETCH_DELAY_MS = 400;
const MAX_PREFETCH_CACHE = 8;

export class ProductQuickView {
  private container: HTMLElement;
  private quickViewPlaceholder: HTMLElement;
  private background: HTMLElement;
  private isOpen: boolean = false;
  private wasCartOpen: boolean = false; // Track if cart drawer was open before quick view opened

  private prefetchCache = new Map<string, { html: string }>();
  private prefetchDelayTimeout: ReturnType<typeof setTimeout> | null = null;
  private prefetchAbortController: AbortController | null = null;
  private static contentCache: Map<string, { html: string; timestamp: number }> = new Map();
  private static readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
  private static readonly MAX_CACHE_ITEMS = 10;

  private static cleanExpiredCache(): void {
    const now = Date.now();
    for (const [key, value] of ProductQuickView.contentCache.entries()) {
      if (now - value.timestamp >= ProductQuickView.CACHE_DURATION_MS) {
        ProductQuickView.contentCache.delete(key);
      }
    }
  }

  // Component instances (similar to product-main.ts)
  private formElement: HTMLFormElement | null = null;
  private form: ProductForm | null = null;
  private wishlistButton: HTMLButtonElement | null = null;
  private currentProductHandle: string | null = null;
  private cartSuccessHandler: ((e: Event) => void) | null = null;

  private boundDocumentClick = (e: Event) => this.handleDocumentClick(e);
  private boundMouseOver = (e: Event) => this.handleMouseOver(e);
  private boundMouseLeave = (e: Event) => this.handleMouseLeave(e);
  private boundKeydown = (e: Event) => this.handleKeydown(e as KeyboardEvent);
  private boundClose = () => this.close();

  constructor() {
    this.container = document.querySelector(SELECTOR_CONTAINER) as HTMLElement;
    this.quickViewPlaceholder = document.querySelector(SELECTOR_QUICK_VIEW_PLACEHOLDER) as HTMLElement;
    this.background = document.querySelector(SELECTOR_BACKGROUND) as HTMLElement;
    
    if (!this.container) {
      throw new Error('Product Quick View container not found');
    }

    this.init();
  }

  private init(): void {
    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    document.addEventListener('click', this.boundDocumentClick);
    document.addEventListener('mouseover', this.boundMouseOver, true);
    document.addEventListener('mouseleave', this.boundMouseLeave, true);
    if (this.background) {
      this.background.addEventListener('click', this.boundClose);
    }
    document.addEventListener('keydown', this.boundKeydown);
  }

  private handleMouseOver(e: Event): void {
    const target = e.target as Node;
    if (!target || !(target instanceof Element)) return;
    const thumbnail = target.closest(SELECTOR_PRODUCT_THUMBNAIL) as HTMLElement | null;
    if (!thumbnail?.querySelector(SELECTOR_QUICK_VIEW_BTN)) return;
    const productId = thumbnail.dataset.productId;
    const handle = this.getHandleFromThumbnail(thumbnail);
    if (!productId || !handle || this.prefetchCache.has(productId)) return;
    if (this.prefetchDelayTimeout) clearTimeout(this.prefetchDelayTimeout);
    this.prefetchDelayTimeout = setTimeout(() => {
      this.prefetchDelayTimeout = null;
      this.preloadQuickView(productId, handle);
    }, PREFETCH_DELAY_MS);
  }

  private handleMouseLeave(e: Event): void {
    const target = e.target as Node;
    if (!target || !(target instanceof Element)) return;
    const thumbnail = target.closest(SELECTOR_PRODUCT_THUMBNAIL);
    if (!thumbnail) return;
    if (this.prefetchDelayTimeout) {
      clearTimeout(this.prefetchDelayTimeout);
      this.prefetchDelayTimeout = null;
    }
    this.prefetchAbortController?.abort();
  }

  private getHandleFromThumbnail(thumbnail: HTMLElement): string | null {
    const handle = thumbnail.dataset.productHandle;
    if (handle) return handle;
    const link = thumbnail.querySelector('a[href*="/products/"]') as HTMLAnchorElement;
    if (link?.href) {
      const h = link.href.split('/products/')[1]?.split('?')[0]?.split('#')[0];
      if (h) return h;
    }
    return null;
  }

  private preloadQuickView(productId: string, handle: string): void {
    if (this.prefetchCache.size >= MAX_PREFETCH_CACHE) return;
    this.prefetchAbortController?.abort();
    this.prefetchAbortController = new AbortController();
    const url = `/products/${handle}?sections=${ProductQuickView.QUICK_VIEW_SECTION_ID}`;
    fetch(url, { signal: this.prefetchAbortController.signal })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Prefetch failed'))))
      .then((data) => {
        const html =
          data[ProductQuickView.QUICK_VIEW_SECTION_ID] ??
          data['product-main'] ??
          (Object.values(data)[0] as string | undefined);
        if (html && typeof html === 'string' && this.prefetchCache.size < MAX_PREFETCH_CACHE) {
          this.prefetchCache.set(productId, { html });
        }
      })
      .catch(() => {});
  }

  private handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Check if clicked element is a quick view button (for opening)
    if (target.matches(SELECTOR_QUICK_VIEW_BTN) || 
        target.closest(SELECTOR_QUICK_VIEW_BTN)) {
      event.preventDefault();
      event.stopPropagation();
      
      const button = target.matches(SELECTOR_QUICK_VIEW_BTN) 
        ? target 
        : target.closest(SELECTOR_QUICK_VIEW_BTN) as HTMLElement;
      
      this.handleQuickViewClick(button);
    }
    
    // Check if clicked element is a close button
    if (target.matches(SELECTOR_CLOSE) || target.closest(SELECTOR_CLOSE)) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  /**
   * Public entry for lazy-loaded section: open quick view for the given trigger button.
   */
  public async openFromTrigger(button: HTMLElement): Promise<void> {
    return this.handleQuickViewClick(button);
  }

  private async handleQuickViewClick(button: HTMLElement): Promise<void> {
    // Get product data from the button's parent product thumbnail
    const productThumbnail = button.closest('[data-product-thumbnail]') as HTMLElement;
    
    if (!productThumbnail) {
      console.warn('Product thumbnail container not found');
      return;
    }

    const productId = productThumbnail.dataset.productId;
    const variantId = productThumbnail.dataset.variantId;

    if (!productId) {
      console.warn('Product ID not found');
      return;
    }

    this.setButtonLoadingState(button, true);
    
    try {
      await this.loadProductContent(productId, variantId);
    } catch (error) {
      console.error('Failed to load product quick view:', error);
      // Show error in the already opened drawer
      this.quickViewPlaceholder.innerHTML = this.getErrorHTML();
      this.quickViewPlaceholder.classList.add('is-error');
      this.open();
    } finally {
      this.setButtonLoadingState(button, false);
    }
  }

  private setButtonLoadingState(button: HTMLElement | null, isLoading: boolean): void {
    if (!button) return;

    const labelEl = button.querySelector(
      '.o-product-thumbnail__quick-view-btn-text, .c-cart-related-product__quick-view-btn-text'
    ) as HTMLElement | null;

    if (isLoading) {
      button.classList.add(CLASS_LOADING);

      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
      } else {
        button.setAttribute('aria-disabled', 'true');
      }

      if (labelEl) {
        if (!button.dataset.quickViewLabel) {
          button.dataset.quickViewLabel = labelEl.textContent || '';
        }
        labelEl.textContent = t('general.quick_view.loading');
      }
    } else {
      button.classList.remove(CLASS_LOADING);

      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
      } else {
        button.removeAttribute('aria-disabled');
      }

      if (labelEl) {
        const originalLabel = button.dataset.quickViewLabel;
        if (originalLabel) {
          labelEl.textContent = originalLabel;
        }
      }
    }
  }

  private static readonly QUICK_VIEW_SECTION_ID = 'product-main';

  private async loadProductContent(productId: string, variantId?: string): Promise<void> {
    try {
      ProductQuickView.cleanExpiredCache();
      const cached = this.prefetchCache.get(productId);
      if (cached) {
        this.prefetchCache.delete(productId);
        await this.loadSectionHTML(cached.html, variantId);
        this.open();
        return;
      }

      const productHandle = await this.getProductHandle(productId);
      this.currentProductHandle = productHandle;

      // Check static content cache for this product handle first
      
      const cacheKey = productHandle;
      const now = Date.now();
      const cachedContent = cacheKey
        ? ProductQuickView.contentCache.get(cacheKey)
        : undefined;

      if (cachedContent && now - cachedContent.timestamp < ProductQuickView.CACHE_DURATION_MS) {
        await this.loadSectionHTML(cachedContent.html, variantId);
        this.open();
        return;
      }

      const sectionUrl = `/products/${productHandle}?sections=${ProductQuickView.QUICK_VIEW_SECTION_ID}`;
      const response = await fetch(sectionUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const sectionsData = await response.json();
      const sectionHTML =
        sectionsData[ProductQuickView.QUICK_VIEW_SECTION_ID] ??
        sectionsData['product-main'] ??
        (Object.values(sectionsData)[0] as string | undefined);

      if (!sectionHTML || typeof sectionHTML !== 'string') {
        throw new Error('Product quick view section not found in response');
      }

      if (cacheKey) {
        ProductQuickView.contentCache.set(cacheKey, {
          html: sectionHTML,
          timestamp: now,
        });

        if (ProductQuickView.contentCache.size > ProductQuickView.MAX_CACHE_ITEMS) {
          const firstKey = ProductQuickView.contentCache.keys().next().value;
          if (firstKey) {
            ProductQuickView.contentCache.delete(firstKey);
          }
        }
      }

      await this.loadSectionHTML(sectionHTML, variantId);
      // Open the quick view only after the section HTML is fully loaded and initialized
      this.open();
    } catch (error) {
      console.error('Error loading product section:', error);
      throw error;
    }
  }

  private async getProductHandle(productId: string): Promise<string> {
    // First try to get handle from the product thumbnail if available
    const productThumbnail = document.querySelector(`[data-product-id="${productId}"]`) as HTMLElement;
    const productLink = productThumbnail?.querySelector('a[href*="/products/"]') as HTMLAnchorElement;
    
    if (productLink) {
      const handle = productLink.href.split('/products/')[1]?.split('?')[0]?.split('#')[0];
      if (handle) return handle;
    }

    return productThumbnail?.dataset.productHandle || '';
  }

  private async loadSectionHTML(sectionHTML: string, variantId?: string): Promise<void> {
    // Set the HTML content (replaces loading state)
    this.quickViewPlaceholder.innerHTML = sectionHTML;
    
    // Add loading class for styling during asset loading
    this.quickViewPlaceholder.classList.add(CLASS_LOADING);
    this.quickViewPlaceholder.querySelector(SELECTOR_PRODUCT_DETAILS)?.classList.add(CLASS_LOADING);
    this.quickViewPlaceholder.querySelector(SELECTOR_PRODUCT_MAIN)?.classList.add(CLASS_LOADING);

    // Wait for critical assets to be fully loaded
    await this.waitForContentToLoad();

    // Initialize components from the loaded section
    this.initializeSectionComponents(variantId);

    await this.waitForLoadingIndicatorToHide();
    
    // Remove loading class for smooth transition
    this.quickViewPlaceholder.classList.remove(CLASS_LOADING);
    this.quickViewPlaceholder.classList.add(CLASS_LOADED);
  }

  private async waitForLoadingIndicatorToHide(): Promise<void> {
    const MAX_WAIT_MS = 5000;
    const startTime = Date.now();
    
    return new Promise<void>((resolve) => {
      const check = () => {
        const loadingEl = this.quickViewPlaceholder.querySelector(SELECTOR_PRODUCT_DETAIL_LOADING);
        const elapsed = Date.now() - startTime;
        
        if (!loadingEl || elapsed >= MAX_WAIT_MS) {
          if (elapsed >= MAX_WAIT_MS) {
            console.warn('Loading indicator timeout - forcing resolution');
          }
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  private async waitForContentToLoad(): Promise<void> {
    // Wait for critical assets in parallel with reasonable timeouts
    const criticalTasks = [
      Promise.race([this.waitForStylesApplied(), this.createTimeout(1000)])
    ];

    // Wait for all critical tasks to complete or timeout
    await Promise.allSettled(criticalTasks);
    
    // Single frame delay to ensure DOM is ready
    await new Promise(resolve => requestAnimationFrame(resolve));
  }

  private async waitForStylesApplied(): Promise<void> {
    // Simple double RAF to ensure styles are computed and applied
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }



  private createTimeout(ms: number): Promise<void> {
    return new Promise<void>((_, reject) => {
      setTimeout(() => reject(new Error('Loading timeout')), ms);
    });
  }



  private initializeSectionComponents(variantId?: string): void {
    // Query for section elements (similar to product-main.ts queryChildren method)
    this.queryQuickViewChildren();
    
    // Initialize components like product-main.ts attachEvents method
    this.attachQuickViewEvents();
    rebindContentDrawerEvents();
    
    // If a specific variant was requested, select it
    if (variantId) {
      this.selectVariant(variantId);
    }
  }

  private queryQuickViewChildren(): void {
    // Query for elements within the loaded section (similar to product-main.ts)
    this.formElement = this.quickViewPlaceholder.querySelector(SEL_FORM_CONTAINER);
    this.wishlistButton = this.quickViewPlaceholder.querySelector(SELECTOR_WISHLIST_BUTTON);
  }

  private attachQuickViewEvents(): void {
    // Initialize ProductForm like in product-main.ts
    if (this.formElement) {
      this.form = new ProductForm(this.formElement);
      // Listen to variant changes
      this.formElement.addEventListener(EVT_VARIANT_CHANGED, this.onVariantChange);
      // ProductForm already handles form submission, no need for custom handler
      
      // Reset quantity selector to 1 when quick view opens (in case it picked up value from cart context)
      if (this.form.qtySelector) {
        this.form.qtySelector.value = 1;
      }
      
      // Add quick view specific behavior: close drawer after successful cart add
      this.addCartSuccessListener();
    }

    // Add event listener for sold out status updates
    this.addSoldOutStatusListener();

    // Initialize wishlist functionality
    rebindWishlist();
    
    // Initialize wishlist button state for current variant
    this.initializeWishlistButton();
  }

  private addCartSuccessListener(): void {
    // Create handler function that can be removed later
    this.cartSuccessHandler = (e: Event) => {
      // Store the cart state before closing (since close() resets it)
      const shouldReopenCart = this.wasCartOpen;
      
      setTimeout(() => {
        this.close();
        // Reopen cart drawer if it was open before quick view opened
        if (shouldReopenCart) {
          this.reopenCartDrawer();
        }
      }, 500);
    };
    
    // Listen for custom event dispatched by ProductForm on successful cart add
    window.addEventListener('cart:item-added', this.cartSuccessHandler);
  }

  private addSoldOutStatusListener(): void {
    // Listen for sold out status updates from swatches
    this.quickViewPlaceholder.addEventListener(EVT_SOLD_OUT_STATUS_UPDATED, this.onSoldOutStatusUpdated);
  }

  private onSoldOutStatusUpdated = (e: Event) => {
    setTimeout(() => {
      this.quickViewPlaceholder.querySelector(SELECTOR_PRODUCT_DETAIL_LOADING)?.remove();
      this.quickViewPlaceholder.querySelector(SELECTOR_PRODUCT_DETAILS)?.classList.remove(CLASS_LOADING);
      this.quickViewPlaceholder.querySelector(SELECTOR_PRODUCT_MAIN)?.classList.remove(CLASS_LOADING);
    }, 1000);
  }

  private selectVariant(variantId: string): void {
    // Logic to select a specific variant in the loaded section
    const variantInputs = this.container.querySelectorAll(`input[value="${variantId}"]`);
    variantInputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    
    // Reset quantity to 1 after variant selection
    if (this.form?.qtySelector) {
      this.form.qtySelector.value = 1;
    }
  }

  private onVariantChange = (e: Event) => {
    // Get the variant details from the dispatched event (like in product-main.ts)
    const { detail } = e as EventVariantChanged;
    const { oldVariant, newVariant } = detail;

    // Handle variant changes in quick view context
    // This could update pricing, availability, images, etc.
    // Intentionally do not change wishlist variant; always use first variant
  }

  private initializeWishlistButton(): void {
    // Initialize wishlist button state when quick view is loaded
    if (this.wishlistButton && this.form && this.form.swatches) {
      const variants = (this.form.swatches as any).data?.variants || [];
      const firstVariantId = variants?.[0]?.id;
      if (firstVariantId) {
        this.wishlistButton.setAttribute('data-variant', firstVariantId.toString());
        this.wishlistButton.classList.remove(CLASS_DISABLED);
      }
    }
  }

  private detachQuickViewEvents(): void {
    // Clean up component instances like in product-main.ts detatchEvents method
    if (this.form) {
      // Clean up the mutation observer for cart success detection
      const observer = (this.form as any)._quickViewObserver;
      if (observer) {
        observer.disconnect();
        delete (this.form as any)._quickViewObserver;
      }
      this.form.detatchEvents();
    }
    if (this.formElement) {
      this.formElement.removeEventListener(EVT_VARIANT_CHANGED, this.onVariantChange);
    }

    // Remove sold out status event listener
    this.quickViewPlaceholder.removeEventListener(EVT_SOLD_OUT_STATUS_UPDATED, this.onSoldOutStatusUpdated);

    // Remove cart success listener
    if (this.cartSuccessHandler) {
      window.removeEventListener('cart:item-added', this.cartSuccessHandler);
      this.cartSuccessHandler = null;
    }

    // Reset references
    this.formElement = null;
    this.form = null;
    this.wishlistButton = null;
    this.currentProductHandle = null;
  }

  private getErrorHTML(): string {
    return `
      <div class="c-product-quick-view-drawer__error">
        <p>${t('general.quick_view.product_loading_error')}</p>
      </div>
    `;
  }

  public open(): void {
    if (this.isOpen) return;

    // Check if cart drawer is open before closing it
    const cartContainer = document.querySelector(SELECTOR_CART_CONTAINER);
    this.wasCartOpen = cartContainer?.classList.contains(CLASS_DRAWER_OPEN) || false;

    // Close cart drawer if it's open (same z-index conflict)
    this.closeCartDrawer();

    this.container.classList.add(CLASS_ACTIVE);
    document.body.classList.add(CLASS_SCROLL_DISABLED);
    this.isOpen = true;

    // Focus management for accessibility
    const firstFocusable = this.quickViewPlaceholder.querySelector('button, input, select, textarea, a[href]') as HTMLElement;
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
  }

  public close(): void {
    if (!this.isOpen) return;

    // Clean up component instances before closing
    this.detachQuickViewEvents();

    this.container.classList.remove(CLASS_ACTIVE);
    document.body.classList.remove(CLASS_SCROLL_DISABLED);
    this.isOpen = false;
    this.wasCartOpen = false; // Reset cart state flag

    // Clear content to free up memory
    setTimeout(() => {
      if (!this.isOpen) {
        this.quickViewPlaceholder.innerHTML = '';
        // Clean up loading/loaded classes
        this.quickViewPlaceholder.classList.remove(CLASS_LOADING, CLASS_LOADED, 'is-error');
      }
    }, 300); // Wait for transition to complete
  }

  private closeCartDrawer(): void {
    // Close the cart drawer by removing the is-drawer-open class
    const cartContainer = document.querySelector(SELECTOR_CART_CONTAINER);
    if (cartContainer && cartContainer.classList.contains(CLASS_DRAWER_OPEN)) {
      cartContainer.classList.remove(CLASS_DRAWER_OPEN);
      // Note: We don't remove CLASS_SCROLL_DISABLED from body here because
      // the quick view will add it back immediately after this call
    }
  }

  private reopenCartDrawer(): void {
    // Reopen the cart drawer by adding the is-drawer-open class
    const cartContainer = document.querySelector(SELECTOR_CART_CONTAINER);
    if (cartContainer) {
      cartContainer.classList.add(CLASS_DRAWER_OPEN);
      document.body.classList.add(CLASS_SCROLL_DISABLED);
    }
  }

  public destroy(): void {
    this.detachQuickViewEvents();
    if (this.prefetchDelayTimeout) clearTimeout(this.prefetchDelayTimeout);
    this.prefetchAbortController?.abort();
    this.prefetchCache.clear();
    document.removeEventListener('click', this.boundDocumentClick);
    document.removeEventListener('mouseover', this.boundMouseOver, true);
    document.removeEventListener('mouseleave', this.boundMouseLeave, true);
    document.removeEventListener('keydown', this.boundKeydown);
    if (this.background) {
      this.background.removeEventListener('click', this.boundClose);
    }
  }
}